import { useQuery, useQueries } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";

const isLocalMediaUrl = (url?: string | null) =>
  !!url && (url.startsWith("blob:") || url.startsWith("data:"));

const looksLikeUrl = (value: string) =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith("//");

const isS3HttpHost = (hostname: string) =>
  /(?:^|\.)s3[.-][a-z0-9-]+\./i.test(hostname);

export const needsSigning = (url?: string | null) => {
  if (!url || isLocalMediaUrl(url)) return false;
  if (!looksLikeUrl(url)) return true;
  if (/^s3:\/\//i.test(url)) return true;
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    return isS3HttpHost(new URL(url).hostname);
  } catch {
    return false;
  }
};

export const signMediaUrl = async (url: string) => {
  if (!needsSigning(url)) return url;
  const { data } = await fetchApi<string>({
    data: { url },
    method: "POST",
    url: "/v1/media/sign",
  });
  return data;
};

export const useSignedMedia = (url?: string | null, forceRetryKey?: string) => {
  const shouldSign = needsSigning(url);
  const { data, isError, isPending } = useQuery({
    queryKey: [`media-${url}`, forceRetryKey],
    queryFn: () => signMediaUrl(url ?? ""),
    enabled: shouldSign,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  return {
    url: shouldSign ? data : (url ?? undefined),
    isError: shouldSign ? isError : false,
    isPending: shouldSign ? isPending : false,
  };
};

export const useBatchSignedMedia = (urls: (string | null | undefined)[]) => {
  const queries = useQueries({
    queries: urls.map((url) => ({
      queryKey: [`media-${url}`],
      queryFn: () => signMediaUrl(url ?? ""),
      enabled: needsSigning(url),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24, // 24 hours
    })),
  });

  return queries.map((query, index) => {
    const source = urls[index];
    const shouldSign = needsSigning(source);
    return {
      url: shouldSign ? query.data : (source ?? undefined),
      isError: shouldSign ? query.isError : false,
      isPending: shouldSign ? query.isPending : false,
    };
  });
};
